import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { RefreshCw, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { logger } from "@/lib/logger";
import { useAuth } from "@/contexts/AuthContext";

const VISIBLE_ROLES = new Set(["admin", "super_admin", "manager", "account_manager"]);

// Default new contract_end → today + 1 year (YYYY-MM-DD)
function defaultRenewalDate() {
  const d = new Date();
  d.setFullYear(d.getFullYear() + 1);
  return d.toISOString().slice(0, 10);
}

export default function RenewalDropdown() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  // Inline date-picker state, keyed by client id.
  const [datePickerFor, setDatePickerFor] = useState(null);
  const [newEnd, setNewEnd] = useState(defaultRenewalDate());
  const [busy, setBusy] = useState(false);

  const visible = user && VISIBLE_ROLES.has(user.role);

  const load = useCallback(async () => {
    if (!visible) return;
    try {
      const { data } = await api.get("/renewals");
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      if (err?.response?.status !== 401 && err?.response?.status !== 403) {
        logger.error("renewals load failed:", err);
      }
    }
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    load();
    const t = setInterval(load, 5 * 60_000); // refresh every 5 min
    return () => clearInterval(t);
  }, [visible, load]);

  if (!visible) return null;

  const count = items.length;

  const markRenewed = async (clientId) => {
    if (!newEnd) {
      toast.error("Pick a new end date");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/clients/${clientId}/mark-renewed`, { contract_end: newEnd });
      toast.success("Client renewed");
      setDatePickerFor(null);
      setNewEnd(defaultRenewalDate());
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Renew failed");
    } finally {
      setBusy(false);
    }
  };

  const createRenewalTask = async (clientId) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/clients/${clientId}/create-renewal-task`);
      toast.success(data?.already_exists ? "Renewal task already open" : "Renewal task created");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not create task");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div className="relative inline-flex">
          <Button
            variant="ghost"
            size="icon"
            aria-label="Client renewals"
            data-testid="renewal-dropdown-trigger"
            className={count > 0 ? "text-teal hover:text-teal" : ""}
          >
            <RefreshCw size={18} strokeWidth={1.5} />
          </Button>
          {count > 0 && (
            <span
              className="absolute top-1 right-1 min-w-4 h-4 px-1 rounded-full bg-[#F59E0B] text-white text-[10px] flex items-center justify-center pointer-events-none"
              data-testid="renewal-count"
            >
              {count > 9 ? "9+" : count}
            </span>
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[360px] p-0 max-h-[480px] overflow-hidden flex flex-col"
        data-testid="renewal-dropdown"
      >
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold">Client Renewals</div>
            <div className="text-[11px] text-muted-foreground">Contracts ending in the next 10 days</div>
          </div>
          <span className="text-xs text-muted-foreground">{count}</span>
        </div>
        <div className="flex-1 overflow-y-auto">
          {count === 0 && (
            <div className="px-4 py-8 text-center text-xs text-muted-foreground" data-testid="renewal-empty">
              No upcoming renewals.
            </div>
          )}
          {items.map((c) => (
            <div
              key={c.id}
              className="px-4 py-3 border-b border-border/60 last:border-b-0"
              data-testid={`renewal-row-${c.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <Link
                  to={`/clients/${c.id}`}
                  onClick={() => setOpen(false)}
                  className="flex-1 min-w-0 hover:text-teal"
                >
                  <div className="text-sm font-medium truncate">{c.client_name}</div>
                  <div className="text-[11px] text-muted-foreground">
                    Ends {c.contract_end?.slice(0, 10)} · {c.days_left} day{c.days_left === 1 ? "" : "s"} left
                  </div>
                </Link>
                {c.open_renewal_task_id && (
                  <span className="text-[10px] uppercase tracking-wider text-amber-600 shrink-0">Task open</span>
                )}
              </div>

              {datePickerFor === c.id ? (
                <div className="mt-2 flex items-center gap-2">
                  <Input
                    type="date"
                    value={newEnd}
                    onChange={(e) => setNewEnd(e.target.value)}
                    className="h-8 text-xs"
                    data-testid={`renewal-date-${c.id}`}
                  />
                  <Button
                    size="sm"
                    onClick={() => markRenewed(c.id)}
                    disabled={busy}
                    data-testid={`renewal-confirm-${c.id}`}
                  >
                    Confirm
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => setDatePickerFor(null)}
                    data-testid={`renewal-cancel-${c.id}`}
                    aria-label="Cancel"
                  >
                    <X size={14} />
                  </Button>
                </div>
              ) : (
                <div className="mt-2 flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs border-teal/40 text-teal hover:bg-teal/10 hover:text-teal"
                    onClick={() => { setDatePickerFor(c.id); setNewEnd(defaultRenewalDate()); }}
                    data-testid={`renewal-mark-${c.id}`}
                  >
                    Mark renewed
                  </Button>
                  {!c.open_renewal_task_id && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs"
                      onClick={() => createRenewalTask(c.id)}
                      disabled={busy}
                      data-testid={`renewal-task-${c.id}`}
                    >
                      Create task
                    </Button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
